import { logs, SeverityNumber } from "@opentelemetry/api-logs";
import { environment } from "environments/environment";
import { List, Map } from "immutable";
import { navigationContext } from "./navigation-context";

type ConsoleLevel = "debug" | "log" | "info" | "warn" | "error";

const SEVERITY: Map<ConsoleLevel, SeverityNumber> = Map<ConsoleLevel, SeverityNumber>({
  debug: SeverityNumber.DEBUG,
  log: SeverityNumber.INFO,
  info: SeverityNumber.INFO,
  warn: SeverityNumber.WARN,
  error: SeverityNumber.ERROR,
});

/** Forwards console output to OneUptime, joining same-severity calls within a task so multi-line banners arrive whole. */
export const captureConsole = (): void => {
  // DevTools would attribute every message to the wrapper instead of its caller.
  if (environment.oneUptime.environment === "local") {
    return;
  }

  let pending: { severity: SeverityNumber; lines: List<string> } | null = null;
  // Stops anything the SDK writes to the console while emitting from being captured again.
  let emitting: boolean = false;

  const flush = (): void => {
    if (!pending) {
      return;
    }

    const { severity, lines } = pending;
    pending = null;

    emitting = true;
    try {
      logs.getLogger("lims-console").emit({
        severityNumber: severity,
        severityText: SeverityNumber[severity],
        body: lines.join("\n").slice(0, 16384),
        attributes: { "log.source": "console" },
        context: navigationContext(),
      });
    } catch {
    } finally {
      emitting = false;
    }
  };

  SEVERITY.filter((severity: SeverityNumber): boolean => severity >= SeverityNumber.WARN || environment.oneUptime.environment === "development").forEach(
    (severity: SeverityNumber, level: ConsoleLevel): void => {
      const original: (...args: unknown[]) => void = console[level].bind(console);
      console[level] = (...args: unknown[]): void => {
        original(...args);
        try {
          // Skips Angular's default ErrorHandler line, which TelemetryErrorHandler already reports.
          if (emitting || (level === "error" && args.length === 2 && args[0] === "ERROR")) {
            return;
          }

          if (pending && pending.severity !== severity) {
            flush();
          }

          if (!pending) {
            pending = { severity, lines: List() };
            // A microtask in Angular's zone runs change detection when it drains, which logs again on every pass.
            Zone.root.run((): void => queueMicrotask(flush));
          }

          pending.lines = pending.lines.push(
            List(args)
              .map((value: unknown): string =>
                // Objects are reduced to their shape so payloads such as DTOs never leave the browser.
                (typeof value === "string"
                  ? value
                  : value instanceof Error
                    ? value.stack || String(value)
                    : Array.isArray(value)
                      ? `Array(${value.length})`
                      : typeof value === "function"
                        ? `function ${value.name}`
                        : value !== null && typeof value === "object"
                          ? `${value.constructor?.name ?? "Object"}{${Object.keys(value).join(",")}}`
                          : String(value)
                ).slice(0, 2048)
              )
              .join(" ")
          );
        } catch {}
      };
    }
  );
};
